import { I18n } from '@calendly/i18n'
import { getTimezoneName } from '@calendly/timezone-tools'
import { BodyText } from '@calendly/ui/components/typography'
import { Fragment, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { timezoneSelector } from 'selectors/current_user'
import { formatDuration } from 'services/event_types'

import { WidgetContainer } from '../widget_container'
import { AvailabilitySummary } from './availability_summary'
import { SpotsSummary } from './spots_summary'

const getDateRangeDescription = ({ event }) => {
  if (event.pendingType === 'AdhocEventType') return null

  const periodType = event.periodType
  if (periodType === 'unlimited') {
    return I18n.t('publisher.event_types.availability.booking_range_fieldset.unlimited_label')
  }

  if (periodType === 'available_moving') {
    const futureDays = (event?.maxBookingTime ?? 0) / 60 / 24
    return I18n.t('publisher.meetings.time_options.available_moving', { days: futureDays })
  }

  if (periodType === 'moving') {
    const futureDays = (event?.maxBookingTime ?? 0) / 60 / 24
    return I18n.t('publisher.meetings.time_options.moving', { days: futureDays })
  }

  if (periodType === 'fixed') {
    const start = new Date(event.startDate)
    const end = new Date(event.endDate)
    return I18n.t('publisher.meetings.time_options.fixed', { start: start.toLocaleDateString(), end: end.toLocaleDateString() })
  }

  return null
}

const getSubtitle = ({ timezone, event }) => {
  const subtitlePieces = [
    getTimezoneName(timezone),
    getDateRangeDescription({ event }),
    formatDuration(event.duration),
  ]

  return (
    <span>
      {subtitlePieces.filter(Boolean).map((piece, index) => (
        <Fragment key={piece}>
          {piece}
          {index < subtitlePieces.length - 1 && <br />}
        </Fragment>
      ))}
    </span>
  )
}

export const TimeOptionsWidget = ({ event }) => {
  const timezone = useSelector(timezoneSelector)
  const isOneOff = useMemo(() => event.pendingType === 'AdhocEventType' && event.bookingMethod === 'instant', [event])
  const isMultiHostEventType = useMemo(() => event?.poolingType != null, [event])

  const availabilityRules = useMemo(() => {
    if (event.availabilityRule) {
      return event.availabilityRule.rules
    }

    if (!isMultiHostEventType) {
      return event.memberships?.[0]?.activeAvailabilityRule?.rules
    }

    return null
  }, [event, isMultiHostEventType])


  return (
    <WidgetContainer title={I18n.t('publisher.meetings.time_options.title')} subtitle={getSubtitle({ timezone, event })}>
      {isOneOff && <SpotsSummary groupedSpots={event.groupedSpots} timezone={timezone} />}
      {!isOneOff && availabilityRules && <AvailabilitySummary availabilityRules={availabilityRules} />}
      {!isOneOff && isMultiHostEventType && <BodyText>{I18n.t('publisher.meetings.time_options.varies_by_host')}</BodyText>}
    </WidgetContainer>
  )
}
